import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useAlerts } from '../context/AlertsContext';
import {
  LayoutDashboard, Zap, Leaf, BarChart3, FileText,
  Grid3X3, ClipboardList, DollarSign, Wrench,
  Settings, Users, ScrollText, ChevronLeft, ChevronRight,
  LogOut, Bell, User, Menu, X, AlertTriangle, Clock
} from 'lucide-react';
import AIChat from './AIChat';
import clsx from 'clsx';

const navItems = [
  { path: '/dashboard',   label: 'Dashboard',      icon: LayoutDashboard },
  { path: '/assets',      label: 'Assets',         icon: Zap             },
  { path: '/carbon',      label: 'Carbon',         icon: Leaf            },
  { path: '/esg',         label: 'ESG Reports',    icon: BarChart3       },
  { path: '/ppa',         label: 'PPA Contracts',  icon: FileText        },
  { path: '/grid',        label: 'Grid Ops',       icon: Grid3X3         },
  { path: '/regulatory',  label: 'Regulatory',     icon: ClipboardList   },
  { path: '/finance',     label: 'Finance',        icon: DollarSign      },
  { path: '/maintenance', label: 'Maintenance',    icon: Wrench          },
  null,
  { path: '/settings',   label: 'Settings',        icon: Settings,  roles: ['super_admin'] },
  { path: '/users',      label: 'Users',           icon: Users,     roles: ['super_admin'] },
  { path: '/audit',      label: 'Audit Log',       icon: ScrollText, roles: ['super_admin'] },
];

export default function AppLayout() {
  const { user, logout }             = useAuth();
  const { settings }                 = useSettings();
  const { criticalAlerts, upcomingDeadlines } = useAlerts();
  const [collapsed, setCollapsed]    = useState(false);
  const [mobileOpen, setMobileOpen]  = useState(false);
  const [notifOpen, setNotifOpen]    = useState(false);
  const location  = useLocation();
  const navigate  = useNavigate();

  const canSee = (item) => !item.roles || item.roles.includes(user?.role);
  const totalBadge = criticalAlerts + upcomingDeadlines;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={clsx('flex items-center gap-3 px-4 py-5 border-b border-white/10', collapsed && 'justify-center')}>
        {settings.logo_url
          ? <img src={settings.logo_url} alt="logo" className="h-8 w-8 rounded object-contain shrink-0"/>
          : <div className="h-8 w-8 rounded bg-primary-500 flex items-center justify-center text-white font-bold text-sm shrink-0">E</div>
        }
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">{settings.app_name || 'EnergyOS ERP'}</p>
            <p className="text-white/40 text-xs truncate">{settings.company_name}</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map((item, i) => {
          if (!item) return <hr key={i} className="border-white/10 my-2"/>;
          if (!canSee(item)) return null;
          const Icon = item.icon;
          const active = location.pathname.startsWith(item.path);
          // Badge on maintenance
          const showBadge = item.path === '/maintenance' && criticalAlerts > 0;
          const showRegBadge = item.path === '/regulatory' && upcomingDeadlines > 0;
          return (
            <NavLink key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
              className={clsx('flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative',
                collapsed && 'justify-center px-2',
                active ? 'bg-primary-500 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
              )}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={18} className="shrink-0"/>
              {!collapsed && <span className="truncate flex-1">{item.label}</span>}
              {(showBadge || showRegBadge) && (
                <span className="h-4 w-4 bg-danger text-white text-xs rounded-full flex items-center justify-center shrink-0 font-bold">
                  {showBadge ? criticalAlerts : upcomingDeadlines}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User */}
      <div className={clsx('border-t border-white/10 p-3', collapsed ? 'flex justify-center' : 'flex items-center gap-3')}>
        <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
          {user?.name?.[0]?.toUpperCase()}
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-medium truncate">{user?.name}</p>
              <p className="text-white/40 text-xs capitalize">{user?.role?.replace(/_/g,' ')}</p>
            </div>
            <button onClick={logout} className="text-white/50 hover:text-white transition-colors" title="Logout">
              <LogOut size={16}/>
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans">
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)}/>}

      {/* Mobile sidebar */}
      <aside className={clsx('fixed inset-y-0 left-0 z-50 w-64 bg-sidebar lg:hidden transform transition-transform duration-200', mobileOpen ? 'translate-x-0' : '-translate-x-full')}>
        <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-white/60 hover:text-white"><X size={20}/></button>
        <SidebarContent/>
      </aside>

      {/* Desktop sidebar */}
      <aside className={clsx('hidden lg:flex flex-col bg-sidebar transition-all duration-200 shrink-0 relative', collapsed ? 'w-16' : 'w-60')}>
        <SidebarContent/>
        <button onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 shadow-sm z-10">
          {collapsed ? <ChevronRight size={12}/> : <ChevronLeft size={12}/>}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0 gap-3">
          <button className="lg:hidden text-gray-500 shrink-0" onClick={() => setMobileOpen(true)}><Menu size={20}/></button>

          {/* Search placeholder — future enhancement */}
          <div className="flex-1 max-w-xs hidden sm:block">
            <div className="text-sm text-gray-400 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200 cursor-not-allowed select-none">
              Search… (coming soon)
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            {/* Alert bell */}
            <div className="relative">
              <button onClick={() => setNotifOpen(o => !o)}
                className="relative h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Bell size={18}/>
                {totalBadge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-danger text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {totalBadge > 9 ? '9+' : totalBadge}
                  </span>
                )}
              </button>

              {/* Notification dropdown */}
              {notifOpen && (
                <div className="absolute right-0 top-10 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-800">Notifications</p>
                    <button onClick={() => setNotifOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={14}/></button>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {criticalAlerts > 0 && (
                      <button onClick={() => { setNotifOpen(false); navigate('/maintenance'); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-left border-b border-gray-50">
                        <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                          <AlertTriangle size={14} className="text-danger"/>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{criticalAlerts} Critical Asset Alert{criticalAlerts > 1 ? 's' : ''}</p>
                          <p className="text-xs text-gray-400">Immediate attention required</p>
                        </div>
                      </button>
                    )}
                    {upcomingDeadlines > 0 && (
                      <button onClick={() => { setNotifOpen(false); navigate('/regulatory'); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-colors text-left border-b border-gray-50">
                        <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                          <Clock size={14} className="text-warn"/>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{upcomingDeadlines} Filing Deadline{upcomingDeadlines > 1 ? 's' : ''} Due Soon</p>
                          <p className="text-xs text-gray-400">Within the next 30 days</p>
                        </div>
                      </button>
                    )}
                    {totalBadge === 0 && (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        <Bell size={20} className="mx-auto mb-2 text-gray-300"/>
                        All clear — no urgent notifications
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User chip */}
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-700">
              <div className="h-7 w-7 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-bold">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <span className="font-medium hidden md:block">{user?.name}</span>
            </div>
          </div>
        </header>

        {/* Page */}
        <main className="flex-1 overflow-y-auto">
          <Outlet/>
        </main>
      </div>

      <AIChat/>
    </div>
  );
}
